/**
 * PublicLoginDialog — the real sign-in flow for a share-link visitor.
 *
 * Rendered as a React SIBLING of `PublicCampaignShell` on `PublicShareViewPage`
 * (never a child — a child would have its own clicks swallowed by the shell's
 * click interceptor). The page owns `open`/`onOpenChange` and passes
 * `useAuth().signIn`/`signInWithGoogle` down as props, plus OTP request/verify
 * callbacks: this package must never import Firebase or `@discovr/common`
 * directly, so the parent supplies every auth call and this component only
 * knows their plain async shapes.
 *
 * Three ways in, one at a time: password (default), Google popup, or an
 * embedded OTP email->code flow (via `useOtpFlow`, the same hook
 * `OtpLoginPage` uses for its full-page equivalent — same steps and
 * resend-cooldown/edit-email behavior, compact dialog chrome instead of a
 * split page). `onGoogleSignIn`/`otp` are both optional — omit either to
 * hide that entry point. `otp`'s two callbacks are bundled into one prop
 * (not two independently-optional ones) so "wire both or neither" is a
 * type-level guarantee, not just a convention both call sites happen to follow.
 *
 * On a successful sign-in (any method), `onSignedIn` just closes the dialog —
 * it does NOT reload. The T4 resolver (`ShortLinkResolverPage`) branches live
 * on `useAuth()`, so once Firebase reports the new session the resolver's
 * authed branch takes over on its own re-render.
 *
 * Invite-only platform: deliberately no create-account / sign-up affordance
 * anywhere in this dialog.
 */
import * as React from 'react';
import { useState } from 'react';
import { CircleNotch, Eye, EyeSlash, PencilSimple, WarningCircle } from '../../design-system/icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { GoogleGlyph } from './GoogleGlyph';
import { useOtpFlow } from './useOtpFlow';

// Same generic, non-enumerating copy as apps/brand & apps/admin LoginPage
// (OWASP Authentication Cheat Sheet): identical for a wrong password, an
// unknown account, or a cross-app rejection — deliberately not shared as a
// cross-app util, matching how each of those two surfaces owns its own copy.
const GENERIC_AUTH_ERROR = 'Invalid email or password.';

function authErrorMessage(err: unknown): string {
  // Duck-typed check only — never imports Firebase, just reads a `.code`
  // string off whatever error `signIn`/`onGoogleSignIn` rejects with.
  const code = (err as { code?: string } | null)?.code ?? '';
  if (code === 'auth/too-many-requests') return 'Too many failed attempts. Please try again later.';
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection.';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return '';
  return GENERIC_AUTH_ERROR;
}

type Mode = 'password' | 'otp';

export interface PublicLoginDialogOtpProps {
  /** Always resolves (enumeration-safe) — the backend never reveals eligibility. */
  onRequestOtp: (email: string) => Promise<void>;
  /** Throws on failure (invalid/expired/max-attempts); resolves once signed in. */
  onVerifyOtp: (params: { email: string; code: string }) => Promise<void>;
}

export interface PublicLoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Parent passes `useAuth().signIn` — this package never imports Firebase. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Called once any sign-in method resolves. The page closes the dialog; see the
   *  module doc above for why that's the whole job — no reload. */
  onSignedIn: () => void;
  /** Parent passes `useAuth().signInWithGoogle`. Omit to hide the button. */
  onGoogleSignIn?: () => Promise<void>;
  /** Omit to hide the "Email me a code instead" option. */
  otp?: PublicLoginDialogOtpProps;
  /** Overrides the "open the full campaign" line (a shared report says report). */
  description?: string;
  /** An error from outside the dialog, e.g. the app's authorization rejecting the account after
   *  `signIn` resolved. Shown until the visitor tries again; the parent clears it. */
  error?: string | null;
}

export function PublicLoginDialog({
  open,
  onOpenChange,
  signIn,
  onSignedIn,
  description = "You're viewing a shared, read-only link. Sign in to open the full campaign.",
  error: externalError = null,
  onGoogleSignIn,
  otp: otpProps,
}: PublicLoginDialogProps): React.ReactElement {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showOtpOption = !!otpProps;
  const otp = useOtpFlow({
    onRequestOtp: otpProps?.onRequestOtp ?? (async () => {}),
    onVerifyOtp: otpProps?.onVerifyOtp ?? (async () => {}),
  });
  // Any in-flight password/Google/OTP call blocks BOTH the dialog's own
  // dismiss (Escape/overlay/X) and switching away from OTP mode — an
  // abandoned-but-still-running requestCode/verifyCode would otherwise apply
  // its state updates (or, worse, a successful onSignedIn) after the visitor
  // already believed they'd cancelled or picked a different method.
  const anyPending = pending || googlePending || otp.requesting || otp.verifying;

  // Reset the whole dialog (both modes) whenever it transitions open -> closed,
  // so a previous attempt (wrong password, half-typed email, mid-OTP) never
  // bleeds into the next time the visitor opens it. Adjust-during-render, same
  // pattern ActionConfirmDialog uses for its reason field.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setMode('password');
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setError(null);
      otp.reset();
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || googlePending) return;
    setPending(true);
    setError(null);
    try {
      await signIn(email, password);
      onSignedIn();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!onGoogleSignIn || googlePending || pending) return;
    setGooglePending(true);
    setError(null);
    try {
      await onGoogleSignIn();
      onSignedIn();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setGooglePending(false);
    }
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.isCodeStep) {
      void otp.verifyCode().then((succeeded) => {
        if (succeeded) onSignedIn();
      });
    } else {
      void otp.requestCode();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !anyPending && onOpenChange(o)}>
      <DialogContent data-testid="public-login-dialog" className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Sign in to Discovr</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {mode === 'otp' ? (
          <form
            onSubmit={handleOtpSubmit}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1">
              {otp.isCodeStep ? (
                <>
                  <span className="text-body-sm-medium text-foreground">Work email</span>
                  <div className="flex h-10 items-center justify-between rounded-lg border border-border bg-muted px-3 text-body-sm-regular text-foreground">
                    <span className="truncate">{otp.email}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={otp.editEmail}
                            aria-label="Edit email"
                            className="size-7 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                          >
                            <PencilSimple size={16} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit email</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </>
              ) : (
                <>
                  <label htmlFor="public-otp-email" className="text-body-sm-medium text-foreground">
                    Work email
                  </label>
                  <Input
                    id="public-otp-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={otp.email}
                    onChange={(e) => otp.setEmail(e.target.value)}
                    disabled={otp.requesting}
                    required
                    autoFocus
                  />
                </>
              )}
            </div>

            {otp.isCodeStep && (
              <div className="flex flex-col gap-1">
                <label htmlFor="public-otp-code" className="text-body-sm-medium text-foreground">
                  6-digit code
                </label>
                <p className="text-caption-regular text-muted-foreground">
                  We sent a 6-digit code to the email above.
                </p>
                <Input
                  id="public-otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={otp.code}
                  onChange={(e) => otp.setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={otp.verifying}
                  required
                  autoFocus
                  className="text-center tracking-[0.3em]"
                />
              </div>
            )}

            {otp.error && (
              <div className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
                <WarningCircle size={15} className="mt-px shrink-0" />
                <span>{otp.error}</span>
              </div>
            )}

            <Button
              type="submit"
              loading={otp.isCodeStep ? otp.verifying : otp.requesting}
              disabled={otp.isCodeStep ? otp.code.trim().length !== 6 : !otp.email.trim()}
              className="w-full"
            >
              {otp.isCodeStep ? 'Sign in' : 'Send code'}
            </Button>

            {otp.isCodeStep && (
              <button
                type="button"
                onClick={() => { void otp.resend(); }}
                disabled={otp.isCoolingDown || anyPending}
                className="text-center text-body-sm-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              >
                {otp.isCoolingDown ? `Resend in ${otp.countdown}s` : 'Resend code'}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setMode('password');
                setError(null);
                otp.reset();
              }}
              disabled={anyPending}
              className="text-center text-body-sm-medium text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Use password instead
            </button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1">
              <label htmlFor="public-login-email" className="text-body-sm-medium text-foreground">
                Email
              </label>
              <Input
                id="public-login-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
                required
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="public-login-password" className="text-body-sm-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <Input
                  id="public-login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={pending}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {(error ?? externalError) && (
              <div className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
                <WarningCircle size={15} className="mt-px shrink-0" />
                <span>{error ?? externalError}</span>
              </div>
            )}

            <Button
              type="submit"
              loading={pending}
              disabled={!email.trim() || !password || googlePending}
              className="w-full"
            >
              Sign in
            </Button>

            {(onGoogleSignIn || showOtpOption) && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-caption-regular text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                {onGoogleSignIn && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { void handleGoogleSignIn(); }}
                    disabled={pending || googlePending}
                    aria-busy={googlePending || undefined}
                    className="w-full"
                  >
                    {googlePending ? (
                      <CircleNotch size={16} className="mr-2 animate-spin" />
                    ) : (
                      <GoogleGlyph size={16} className="mr-2" />
                    )}
                    Continue with Google
                  </Button>
                )}
                {showOtpOption && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('otp');
                      setError(null);
                    }}
                    disabled={anyPending}
                    className="text-center text-body-sm-medium text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Email me a code instead
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
